import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  keyframes,
  InputAdornment,
  Tooltip,
  useTheme
} from '@mui/material';
import { Send, AutoAwesome, StopCircle, DeleteOutline, Architecture } from '@mui/icons-material';

// Typing indicator animation
const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-6px);
  }
`;

const TypingIndicator = () => (
  <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 4,
          height: 4,
          borderRadius: '50%',
          bgcolor: 'white',
          animation: `${bounce} 1.4s ease-in-out infinite`,
          animationDelay: `${i * 0.16}s`,
        }}
      />
    ))}
  </Box>
);

function MessageInput({ input, setInput, sendMessage, loading, handleStop, onClearChat, hasMessages, cadMode = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = cadMode ? '#8b5cf6' : '#667eea';
  const gradientStart = cadMode ? '#8b5cf6' : '#667eea';
  const gradientEnd = cadMode ? '#6366f1' : '#764ba2';

  return (
    <Box sx={{
      display: 'flex',
      gap: 0.75,
      alignItems: 'flex-end',
      p: 1,
      borderRadius: 2,
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.2s ease',
      flexShrink: 0,
      '&:focus-within': {
        borderColor: primaryColor,
        boxShadow: `0 2px 12px ${cadMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(102, 126, 234, 0.15)'}`,
      }
    }}>
      {/* Clear Chat Button */}
      <Tooltip title="Clear chat">
        <span>
          <IconButton
            onClick={onClearChat}
            disabled={!hasMessages || loading}
            size="small"
            sx={{
              width: 32,
              height: 32,
              color: hasMessages && !loading ? 'text.secondary' : 'action.disabled',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
              },
            }}
          >
            <DeleteOutline sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <TextField
        fullWidth
        size="small"
        placeholder={cadMode ? "Describe the CAD operation..." : "Ask e-pilot anything..."}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !loading && sendMessage()}
        multiline
        maxRows={2}
        disabled={loading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {cadMode ? (
                <Architecture sx={{ fontSize: 14, color: '#8b5cf6', opacity: 0.6 }} />
              ) : (
                <AutoAwesome sx={{ fontSize: 14, color: '#667eea', opacity: 0.6 }} />
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused fieldset': {
              border: 'none',
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '0.85rem',
            py: 0.75,
            '&::placeholder': {
              color: 'text.secondary',
              opacity: 0.7,
            },
          },
        }}
      />

      {/* Send / Stop Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <IconButton
          onClick={sendMessage}
          disabled={!input.trim() || loading}
          sx={{
            background: !input.trim() || loading
              ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'
              : `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
            color: 'white',
            width: 36,
            height: 36,
            borderRadius: 2,
            transition: 'all 0.2s ease',
            boxShadow: !input.trim() || loading
              ? 'none'
              : `0 2px 6px ${cadMode ? 'rgba(139, 92, 246, 0.4)' : 'rgba(102, 126, 234, 0.4)'}`,
            '&:hover': {
              background: !input.trim() || loading
                ? 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'
                : cadMode
                  ? 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)'
                  : 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              transform: !input.trim() || loading ? 'none' : 'scale(1.05)',
            },
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.6)',
            }
          }}
        >
          {loading ? <TypingIndicator /> : <Send sx={{ fontSize: 18 }} />}
        </IconButton>

        {/* Stop Button - appears below send when loading */}
        {loading && (
          <Tooltip title="Stop generating">
            <IconButton
              onClick={handleStop}
              size="small"
              sx={{
                width: 36,
                height: 24,
                borderRadius: 1,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                },
              }}
            >
              <StopCircle sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

export default MessageInput;
